import { enrichItemWithOtherInfo, formatDate } from '@moko/utils';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  PagedDataGrid,
  FilterType,
  FilterOperator,
  buildMetadataFilterColumns,
  parseFilterClauses,
  stringifyFilterClauses,
  StatusBadge,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
} from '@moko/ui';
import { FontIcon, Link } from '@fluentui/react';
import { Cube24Regular } from '@fluentui/react-icons';
import { useBoolean } from '@fluentui/react-hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocation, useNavigate } from 'react-router-dom';
import AppContext from '../AppContext';
import {
  deleteAsset,
  getAssets,
  getAssetStatuses,
  type IAsset,
  type IAssetStatus,
} from '../../services/assetServices';
import { getChartsData } from '../../services/dashboardService';
import {
  getMetadata,
  type IField,
  type ILookup,
} from '../../services/metadataService';
import { appRoles } from '../../authConfig';
import { profileDataAtom } from '../../atoms/authAtoms';
import { currentTenantNameAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  chatCommandContentsAtom,
  defaultChatCommand,
} from '../../atoms/chatCommandAtoms';
import AssetSchedulePanel, {
  type IAssetSchedulePanelRefType,
} from './AssetSchedulePanel';
import ConfirmDialog from '../../common/ConfirmDialog';
import { useFeatureFlag, Features } from '../../libs/featureAccess';

type IAssetItem = IAsset & { [key: string]: any };

const mergeFilters = (
  a: string | undefined,
  b: string | undefined
): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  try {
    return stringifyFilterClauses([
      ...parseFilterClauses(a),
      ...parseFilterClauses(b),
    ]);
  } catch {
    return a;
  }
};

export interface IAssetsListParams {
  isInWorkContext?: boolean;
  contextTabTitle?: string;
  contextBackPath?: string;
  assetIds?: number[];
  onAddAssetClick?: () => void;
  onRemoveAssetClick?: (assetId: number) => void;
  onCreateAssetClick?: () => void;
  onImportAssetClick?: () => void;
}

const AssetsListV2 = ({
  assetIds,
  isInWorkContext,
  contextTabTitle,
  contextBackPath,
  onAddAssetClick,
  onRemoveAssetClick,
  onCreateAssetClick,
  onImportAssetClick,
}: IAssetsListParams) => {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const action = query.get('action');

  const context = useContext(AppContext);
  const profileData = useAtomValue(profileDataAtom);
  const currentTenantName = useAtomValue(currentTenantNameAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setChatCommandContents = useSetAtom(chatCommandContentsAtom);

  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);
  const assetSchedulePanelRef = useRef<IAssetSchedulePanelRefType>(null);

  const [selectedAsset, setSelectedAsset] = useState<IAssetItem | null>(null);
  const [assetStatuses, setAssetStatuses] = useState<IAssetStatus[]>([]);
  const [metadataFields, setMetadataFields] = useState<IField[]>([]);
  const [metadataLookups, setMetadataLookups] = useState<ILookup[]>([]);

  const {
    isTenantReady: categoriesFeatureReady,
    isEnabled: categoriesFeatureEnabled,
  } = useFeatureFlag(Features.FEATURE_CATEGORIES);
  const { isTenantReady: zonesFeatureReady, isEnabled: zonesFeatureEnabled } =
    useFeatureFlag(Features.FEATURE_ZONES);
  const showCategoryColumn = categoriesFeatureReady && categoriesFeatureEnabled;
  const showZoneColumn = zonesFeatureReady && zonesFeatureEnabled;

  const [
    isDeleteConfirmOpen,
    { setTrue: showDeleteConfirm, setFalse: hideDeleteConfirm },
  ] = useBoolean(false);
  const [
    isSchedulePanelOpen,
    { setTrue: showSchedulePanel, setFalse: hideSchedulePanel },
  ] = useBoolean(false);

  useEffect(() => {
    const abortController = new AbortController();
    const loadMeta = async () => {
      setIsInProgress(true);
      try {
        const [statuses, metadata] = await Promise.all([
          getAssetStatuses(abortController),
          getMetadata(abortController, 'Asset'),
        ]);
        setAssetStatuses(Array.isArray(statuses) ? statuses : []);
        setMetadataFields(metadata?.fields ?? []);
        setMetadataLookups(metadata?.lookups ?? []);

        const chartData = await getChartsData(abortController, 'assets', '');
        context.setSelectedTab(
          isInWorkContext ? contextTabTitle ?? 'Work Assets' : 'Assets',
          chartData?.values,
          {
            xAxisFormat: (x: string) =>
              `${x.substring(0, 4)}/${x.substring(4)}`,
          }
        );
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
    };
    loadMeta();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantName]);

  useEffect(() => {
    if (action === 'schedule' && selectedAsset) {
      assetSchedulePanelRef.current?.fetchScheduledAction(
        selectedAsset as IAsset,
        'schedule'
      );
      showSchedulePanel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action]);

  const isAdmin = profileData.roles.includes(appRoles.Admin);
  const hasSelection = !!selectedAsset;

  const otherInfoColumns = useMemo<DataGridColumn<IAssetItem>[]>(
    () =>
      metadataFields
        .filter((mf) => mf.showInGrid)
        .map((mf) => ({
          key: mf.name,
          name: mf.name,
          minWidth: 80,
          width: 150,
          ellipsis: true,
          ...(mf.fieldType === 'Date' && {
            value: (item: IAssetItem) =>
              item[mf.name] ? formatDate(String(item[mf.name])) : '',
          }),
          ...(mf.fieldType === 'DateTime' && {
            value: (item: IAssetItem) =>
              item[mf.name] ? formatDate(String(item[mf.name]), true) : '',
          }),
          ...(mf.fieldType === 'AutoComplete' && {
            value: (item: IAssetItem) =>
              (item[mf.name] ?? ':').split(':')[1] ?? '',
          }),
          ...(mf.fieldType === 'Image' && {
            renderCell: (item: IAssetItem) => (
              <img src={item[mf.name]} style={{ width: 20 }} alt="" />
            ),
          }),
        })),
    [metadataFields]
  );

  const otherInfoFieldNames = useMemo(
    () => otherInfoColumns.map((c) => c.name ?? ''),
    [otherInfoColumns]
  );

  const customFilterColumns = useMemo(
    () => buildMetadataFilterColumns(metadataFields, metadataLookups),
    [metadataFields, metadataLookups]
  );

  const assetStatusOptions = useMemo(
    () =>
      (assetStatuses ?? []).map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [assetStatuses]
  );

  const filterColumns: IFilterableColumn[] = useMemo(
    () => [
      { key: 'id', label: 'Id', type: FilterType.String },
      { key: 'code', label: 'Code', type: FilterType.String },
      { key: 'name', label: 'Name', type: FilterType.String },
      {
        key: 'statusId',
        label: 'Status',
        type: FilterType.Number,
        operators: [
          FilterOperator.Eq,
          FilterOperator.NEq,
          FilterOperator.NNul,
          FilterOperator.In,
        ],
        options: assetStatusOptions,
      },
      { key: 'parentName', label: 'Parent', type: FilterType.String },
      ...customFilterColumns,
    ],
    [assetStatusOptions, customFilterColumns]
  );

  const baseColumns: DataGridColumn<IAssetItem>[] = useMemo(
    () => [
      {
        key: 'id',
        name: 'Id',
        width: 50,
        isSortable: true,
        renderCell: (item: IAssetItem) => (
          <Link onClick={() => navigate(`/editAsset?id=${item.id}`)}>
            {item.id}
          </Link>
        ),
      },
      {
        key: 'code',
        name: 'Code',
        width: 100,
        isSortable: true,
      },
      {
        key: 'name',
        name: 'Name',
        width: 250,
        isSortable: true,
      },
      ...(showCategoryColumn
        ? [
            {
              key: 'category',
              name: 'Category',
              width: 150,
              isSortable: true,
            } as DataGridColumn<IAssetItem>,
          ]
        : []),
      {
        key: 'changeDate',
        name: 'Asset Date',
        width: 175,
        isSortable: true,
        type: 'datetime',
      },
      {
        key: 'parentName',
        name: 'Parent',
        width: 60,
        isSortable: true,
      },
      {
        key: 'statusId',
        name: 'Status',
        width: 120,
        renderCell: (item: IAssetItem) => (
          <StatusBadge
            statuses={assetStatuses ?? []}
            statusId={item.statusId}
          />
        ),
      },
      ...(showZoneColumn
        ? [
            {
              key: 'zoneName',
              name: 'Zone',
              width: 150,
              isSortable: true,
            } as DataGridColumn<IAssetItem>,
          ]
        : []),
    ],
    [assetStatuses, showCategoryColumn, showZoneColumn, navigate]
  );

  const columns = useMemo(
    () => [...baseColumns, ...otherInfoColumns],
    [baseColumns, otherInfoColumns]
  );

  const assetIdsFilter = useMemo(
    () =>
      assetIds
        ? JSON.stringify({
            filters: [
              {
                column: 'Id',
                operation: 'In',
                value: `(${assetIds.join(',')})`,
                type: 'Number',
              },
            ],
          })
        : undefined,
    [assetIds]
  );

  useEffect(() => {
    if (!isInWorkContext) {
      return;
    }
    pagedDataGridRef.current?.refresh();
  }, [assetIdsFilter, isInWorkContext]);

  const commandBarItems: ICommandBarItem[] = useMemo(() => {
    if (isInWorkContext) {
      return [
        {
          key: 'cancel',
          text: 'Cancel',
          icon: <FontIcon iconName="ErrorBadge" style={{ color: '#a4262c' }} />,
          onClick: () => navigate(contextBackPath ?? '/works'),
        },
        {
          key: 'refresh',
          text: 'Refresh',
          icon: <FontIcon iconName="Refresh" />,
          onClick: (ctx) => ctx.refresh(),
        },
        {
          key: 'addItem',
          text: 'Link existing',
          icon: <FontIcon iconName="Link" />,
          onClick: () => onAddAssetClick?.(),
          disabled: !isAdmin,
        },
        {
          key: 'removeItem',
          text: 'Unlink',
          icon: <FontIcon iconName="Cancel" />,
          onClick: () =>
            selectedAsset && onRemoveAssetClick?.(selectedAsset.id),
          disabled: !isAdmin || !hasSelection,
        },
        {
          key: 'createAsset',
          text: 'Create new asset',
          icon: <FontIcon iconName="Add" />,
          onClick: () => onCreateAssetClick?.(),
          disabled: !isAdmin,
        },
        {
          key: 'importAssets',
          text: 'Import assets',
          icon: <FontIcon iconName="Import" />,
          onClick: () => onImportAssetClick?.(),
          disabled: !isAdmin,
        },
      ];
    }

    return [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newItem',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newAsset'),
        disabled: !isAdmin,
      },
      {
        key: 'showQR',
        text: 'Show QR',
        icon: <FontIcon iconName="QRCode" />,
        onClick: () => {
          if (selectedAsset) {
            navigate(`/showQR?assetId=${selectedAsset.id}`);
          }
        },
        disabled: !hasSelection,
      },
      {
        key: 'assetEdit',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => {
          if (selectedAsset) {
            navigate(`/editAsset?id=${selectedAsset.id}`);
          }
        },
        disabled: !isAdmin || !hasSelection,
      },
      {
        key: 'schedule',
        text: 'Schedule',
        icon: <FontIcon iconName="ScheduleEventAction" />,
        onClick: () => {
          if (selectedAsset) {
            showSchedulePanel();
            assetSchedulePanelRef.current?.fetchScheduledAction(
              selectedAsset as IAsset
            );
          }
        },
        disabled: !isAdmin || !hasSelection,
      },
      {
        key: 'assetAudit',
        text: 'Changes',
        icon: <FontIcon iconName="ComplianceAudit" />,
        onClick: () => {
          if (selectedAsset) {
            navigate(`/assetAudit?itemId=${selectedAsset.id}`);
          }
        },
        disabled: !isAdmin || !hasSelection,
      },
      {
        key: 'assetDelete',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: () => showDeleteConfirm(),
        disabled: !isAdmin || !hasSelection,
      },
    ];
  }, [
    isInWorkContext,
    isAdmin,
    hasSelection,
    selectedAsset,
    navigate,
    contextBackPath,
    onAddAssetClick,
    onRemoveAssetClick,
    onCreateAssetClick,
    onImportAssetClick,
    showDeleteConfirm,
    showSchedulePanel,
  ]);

  return (
    <div style={{ maxWidth: 1200 }}>
      <PagedDataGrid<IAssetItem>
        ref={pagedDataGridRef}
        columns={columns}
        filterEnabled
        filterColumns={filterColumns}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          const mergedFilters = mergeFilters(filters, assetIdsFilter);
          const data = await getAssets(
            abortController,
            pageSize,
            pageNo,
            orderBy,
            search,
            mergedFilters
          );
          return {
            ...data,
            items: data.items.map((asset) =>
              enrichItemWithOtherInfo({ ...asset } as any, otherInfoFieldNames)
            ) as IAssetItem[],
          };
        }}
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedAsset(selection);
          context.setSelectedItem(selection);
        }}
        onRowDoubleClicked={(selection) => {
          navigate(`/editAsset?id=${selection.id}`);
        }}
        listHeigh={525}
        otherInfoFieldNames={otherInfoFieldNames}
        loadingConfig={{
          message: 'Loading assets...',
          icon: (
            <Cube24Regular
              style={{ width: 125, height: 125, color: 'lightgray' }}
            />
          ),
        }}
      />
      <AssetSchedulePanel
        ref={assetSchedulePanelRef}
        isSchedulePanelOpen={isSchedulePanelOpen}
        hideSchedulePanel={() => {
          navigate('/assets');
          hideSchedulePanel();
          setChatCommandContents(defaultChatCommand);
        }}
      />
      <ConfirmDialog
        isModalOpen={isDeleteConfirmOpen}
        hideModal={hideDeleteConfirm}
        message={`The Asset ${selectedAsset?.id} would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteConfirm();
          if (!selectedAsset) return;
          const abortController = new AbortController();
          deleteAsset(abortController, selectedAsset.id)
            .then(() => {
              pagedDataGridRef.current?.refresh();
              setSuccessMessage(
                `Item ${selectedAsset.id} successfully deleted`
              );
            })
            .catch((error) => {
              setErrorMessage(error.message);
            });
        }}
      />
    </div>
  );
};

export default AssetsListV2;
